import React, { useState, useEffect } from "react";
import logo from "../assets/imgs/logo.png";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, varifyCaptcha } from "../redux/slices/authSlice";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import toast from "react-hot-toast";

const Login = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const dispatch = useDispatch();
  const { user, loading, error } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // useEffect(() => {
  //   console.log("API URL:", import.meta.env.VITE_API_BASE_URL);
  // }, []);

  useEffect(() => {
    if (user) {
      navigate("/users");
    }
    if (error) {
      setErrors((prev) => ({ ...prev, general: error }));
    }
  }, [user, error, navigate]);

  const handleOnChange = (e) => {
    const { id, value } = e.target;
    if (id === "username") setUsername(value);
    if (id === "password") setPassword(value);

    // Remove error on change if user is fixing input
    setErrors((prev) => ({ ...prev, [id]: "" }));
  };

  const handleBlur = (e) => {
    const { id, value } = e.target;
    setTouched((prev) => ({ ...prev, [id]: true }));

    if (!value.trim()) {
      setErrors((prev) => ({ ...prev, [id]: "This field is required" }));
    } else if (id === "username") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        setErrors((prev) => ({ ...prev, [id]: "Enter a valid email" }));
      }
    }
  };

  const handleOnSubmit = async (e) => {
    e.preventDefault();

    let formErrors = {};

    if (!username.trim()) formErrors.username = "Email is required";
    else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(username))
        formErrors.username = "Enter a valid email";
    }

    if (!password.trim()) formErrors.password = "Password is required";

    setErrors(formErrors);
    setTouched({ username: true, password: true });

    if (Object.keys(formErrors).length > 0) return;

    if (!executeRecaptcha) {
      toast.error("reCAPTCHA not ready");
      return;
    }

    try {
      // ✅ STEP 1
      const captchaToken = await executeRecaptcha("login");

      if (!captchaToken) {
        toast.error("Captcha failed. Try again.");
        return;
      }

      // console.log("Captcha Token:", captchaToken);

      // ✅ STEP 2
      const captchaRes = await dispatch(
        varifyCaptcha({
          token: captchaToken,
          action: "login",
        }),
      ).unwrap();

      // console.log("Captcha Response:", captchaRes);

      // ✅ STEP 3
      if (!captchaRes || !captchaRes.success) {
        toast.error("Captcha verification failed");
        return;
      }

      // ✅ STEP 4 LOGIN
      await dispatch(
        loginUser({
          username: username.trim(),
          password: password.trim(),
        }),
      ).unwrap();
      toast.success("Login successfully !");
    } catch (err) {
      toast.error(
        typeof err === "string" ? err : err?.message || "Something went wrong",
      );
    }
  };

  return (
    <section className="bg-[#ff69431a] min-h-screen flex justify-center items-center px-4">
      <div className="bg-white shadow-lg rounded-xl p-8 w-full max-w-sm flex flex-col items-center text-center">
        <img src={logo} alt="logo" className="w-32 h-auto mb-4" />
        <h1 className="text-2xl font-semibold text-gray-800 mb-1">
          Login to Account
        </h1>
        <p className="text-gray-500 mb-6 text-sm">
          Please log in to access your account and continue your work.
        </p>

        <form className="w-full text-left space-y-4" onSubmit={handleOnSubmit}>
          {/* Username / Email */}
          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              type="email"
              id="username"
              value={username}
              onChange={handleOnChange}
              onBlur={handleBlur}
              placeholder="Enter your email"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 
                ${
                  errors.username
                    ? "border-red-500 focus:ring-red-500"
                    : touched.username
                      ? "border-green-500 focus:ring-green-500"
                      : "border-gray-300 focus:ring-[#ff6943]"
                }`}
            />
            {errors.username && (
              <p className="text-red-500 text-xs mt-1">{errors.username}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <div className="relative w-full">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="off"
                id="password"
                value={password}
                onChange={handleOnChange}
                onBlur={handleBlur}
                placeholder="Enter password"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 pr-10
                  ${
                    errors.password
                      ? "border-red-500 focus:ring-red-500"
                      : touched.password
                        ? "border-green-500 focus:ring-green-500"
                        : "border-gray-300 focus:ring-[#ff6943]"
                  }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-[#ff6943] transition-colors"
              >
                {showPassword ? (
                  <EyeOff size={20} />
                ) : (
                  <Eye size={20} className="cursor-pointer" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`block w-full relative cursor-pointer overflow-hidden font-semibold py-2 rounded-md border border-[#ff6943] text-center transition-all duration-500 ease-out
    ${loading ? "bg-[#ff6943] text-white cursor-not-allowed" : "text-[#ff6943]"}
    before:content-[''] before:absolute before:inset-0 before:bg-[#ff6943]
    before:-translate-x-full before:transition-transform before:duration-500 before:ease-out
    hover:before:translate-x-0 hover:text-white
  `}
          >
            <span className="relative z-10 flex justify-center items-center gap-2">
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                "Sign In"
              )}
            </span>
          </button>

          {/* General error */}
          {errors.general && (
            <p className="text-red-500 text-sm mt-2 text-center">
              {errors.general}
            </p>
          )}
        </form>
      </div>
    </section>
  );
};

export default Login;
